// A page has a HEAD: what this is, and the one thing you came here to do.
//
// The screens rendered a bare `h2` and then went straight into content, so the primary
// action — request leave, add a line, file a declaration — was wherever its form
// happened to sit, usually below everything else on the page. Somebody arriving to do
// the thing had to read the whole screen to find out where to do it.
//
// # A PAGE FILLS THE AREA. The MEASURE belongs to prose, not to the page
//
// This was wrong twice before it was right. First the shell capped every screen at one
// column width; then the page did, and picked between a capped layout and a full one by
// what it held. Both are the same mistake at different addresses: a screen was deciding
// how wide the WINDOW may be, on behalf of the one paragraph on it.
//
// Measured at 1617px: the session screen's panel stopped at x=1288 with three hundred
// pixels of canvas beside it, because that screen holds facts and `column` capped it.
// Nothing on the screen explained the gap, and the screen next to it did not have one.
//
// So a page fills what it is given, always, and the things that genuinely need a
// reading measure cap THEMSELVES — the lede does, `Nothing` does, `Facts` does. That is
// where the constraint belongs: a line of prose has a length beyond which it is hard to
// read, and a table has no such thing. Nothing else in the product has to know.
//
// Two layouts, and a screen only picks when it manages its own regions:
//
//   `page` (default)  the frame, with the page inset.
//   `flush`           the frame with NO inset, for a screen laying out its own regions
//                     — the inbox's list-and-detail split.
//
// Not a shadcn component, because shadcn has no page header — it ships controls, and
// where a page's title sits is this product's decision.

import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { Breadcrumb } from "./breadcrumb";
import { Explains } from "./explains";
import { ModuleNavToggle } from "./module-nav";

export type PageLayout = "page" | "flush";

interface PageProps {
  readonly title: string;
  /**
   * One sentence under the title, when the screen needs a standing caveat — what this
   * page will not do, or what a figure on it means. Not a description of the obvious.
   */
  readonly lede?: string;
  /**
   * WHY THE SCREEN IS THE WAY IT IS — on hover and on focus, never on the surface.
   *
   * `Labelled.why` one level up, and the same test with one extra clause. A lede is read
   * ONCE, on arrival, by somebody who has just navigated; the question is therefore not
   * "does this explain" but **does somebody who has just landed here need it BEFORE they
   * can act** — and the answer moves with how often the screen is visited. `/setup/*` is
   * once per company and its ledes orient a reader who has never seen the screen;
   * `/assets` and `/leave` are daily, and a paragraph there has been read four hundred
   * times.
   *
   * What stays in `lede`: a bound, a refusal, which rows this caller is being shown, how
   * to READ a figure on the screen. What comes here: the statutory citation, the case law,
   * the argument for why riksu derives a figure the way it does.
   *
   * The trigger is a tab stop and that cost is deliberate — see `explains.tsx`. Two of
   * these on one page is a page that explains itself twice.
   */
  readonly why?: string;
  /** The primary action, and at most one or two others. */
  readonly actions?: ReactNode;
  /**
   * WHAT THIS PAGE IS ABOUT RIGHT NOW — a date, a period, a person, a leave year.
   *
   * It sits beside the title rather than in the toolbar, and the distinction is worth
   * holding: a filter narrows a list somebody is already looking at, and this changes
   * WHICH list it is. An attendance screen for the 15th and one for the 16th are two
   * answers to the same question, not one answer filtered two ways — so the control that
   * moves between them belongs to the heading, and the toolbar's filters apply within
   * whichever one is showing.
   *
   * Getting this wrong is how a screen ends up with a date in the filter bar and
   * somebody wondering why clearing the filters did not clear it.
   */
  readonly context?: ReactNode;
  /**
   * The sibling surfaces of this module, if it has any.
   *
   * Declared and rendered here so every module's tabs sit in one place at one size —
   * the alternative is each screen placing its own strip and three of them disagreeing
   * about whether the count goes in the tab or beside it.
   */
  readonly tabs?: ReactNode;
  readonly layout?: PageLayout;
  /** Social/profile surfaces use a centred reading canvas instead of the full data width. */
  readonly measure?: "full" | "social";
  /** Keep scrolling available while allowing an immersive surface to conceal the rail. */
  readonly scrollbar?: "auto" | "hidden";
  readonly children: ReactNode;
}

/**
 * ONE INSET, and it is the same one in both layouts.
 *
 * Measured across seven screens before this existed: a title landed at x=124, x=96 or
 * x=80 and at y=104, y=96 or y=68 depending on which layout the screen had picked, so
 * moving between two screens of one product moved the words.
 */
const INSET = "px-4 py-8 lg:px-10 lg:py-10";

const FRAME: Record<PageLayout, string> = {
  page: `w-full ${INSET}`,
  flush: "flex min-h-0 w-full flex-1 flex-col",
};

export function Page({
  title,
  lede,
  why,
  actions,
  context,
  tabs,
  layout = "page",
  measure = "full",
  scrollbar = "auto",
  children,
}: PageProps) {
  const flush = layout === "flush";

  return (
    <section
      data-page-scrollbar={scrollbar}
      className={cn(
        FRAME[layout],
        !flush && "flex flex-col gap-6",
        !flush &&
          measure === "social" &&
          "mx-auto max-w-[calc(var(--frame-measure)-var(--frame-sidebar))]",
      )}
    >
      <div
        className={cn(
          "flex flex-col gap-2 border-b border-border",
          // THE SAME HORIZONTAL INSET, so a flush screen's title starts where every
          // other title starts. The vertical stays compact on purpose: this is a strip
          // above two scrolling regions rather than the top of a scrolling page, and
          // giving it the page's 40px would spend a fourteenth of the window on a
          // heading nobody is reading twice.
          flush ? "shrink-0 px-4 py-4 lg:px-10" : "pb-3",
        )}
      >
        {/* WHERE THE BREADCRUMB GOES, and why it is HERE rather than anywhere else.
         *
         * Above the title, which is where a breadcrumb conventionally sits, and inside
         * `Page` for two reasons that are this file's own.
         *
         * ONE: the inset. This file exists partly because a title landed at x=124, x=96
         * or x=80 depending on which layout a screen had picked, and `INSET` is the one
         * answer to that. A trail rendered by the shell — above `<Routes>`, say — would
         * be outside it and would have to re-derive it, in both layouts, which is the
         * same defect one line higher up the page.
         *
         * TWO: it is a SIBLING of the title row, not a child of the title column. The
         * row below is `items-start`, so the primary action aligns with the top of that
         * column; putting the trail inside it would drag "Pozisyon ekle" up to the
         * breadcrumb's baseline and leave it floating above the heading it belongs to.
         *
         * It is rendered rather than passed as a prop, because it is DERIVED — from the
         * address and from `destinations.ts`. A `breadcrumb` prop would be a screen
         * telling the shell where it is, and a screen that can say so can say something
         * else.
         *
         * `flush` is not special-cased. It renders in both layouts and costs that strip
         * one 16px caption line, which is a cheap price for knowing where you are.
         *
         * THE REASON HERE USED TO BE FALSE, and it is replaced rather than deleted. It read
         * "today it costs it nothing, because the only flush screen is the inbox and
         * `moduleFor` does not resolve it — the inbox is an ALONGSIDE surface and is
         * deliberately in neither module list." `moduleFor` searches
         * `[...DESTINATIONS, ...ALONGSIDE]` and says so in its own docstring: searching only
         * `DESTINATIONS` "left `/inbox` and `/notifications` resolving to nothing", which was
         * fixed there. So the inbox DOES resolve, and it draws a trail like any other module
         * — visible in the running product as "Alongside your work › Inbox".
         *
         * The rule stands and only its ground moves: a flush screen renders the trail in both
         * layouts, and what that costs is one caption line rather than nothing. */}
        <Breadcrumb />
        {/* Wraps on a narrow screen rather than squeezing the title into two words: the
            action drops under the heading, which is the same reading order. */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex min-w-0 flex-col gap-1">
            {/* THE TITLE AND ITS CONTEXT ON ONE LINE. "Devam" for the 15th of October is
                one heading with a control in it, not a heading and a widget that happen
                to be adjacent — so they share a baseline and wrap together. */}
            <div className="flex flex-wrap items-center gap-3">
              {/* THE SIDEBAR'S TOGGLE, which renders NOTHING when the module has no
                  inner screens. It sits here rather than in the shell chrome because
                  this is where somebody looking at content reaches to get more room for
                  it — and it is the page's control over the shell's state, which is why
                  the state is a context and not a `useState` beside this button. */}
              <ModuleNavToggle />
              <h2 className="m-0 text-[length:var(--type-shell-title-size)] leading-[var(--type-shell-title-leading)] font-medium tracking-[var(--type-shell-title-tracking)] text-foreground">
                {title}
              </h2>
              {/* A SIBLING OF THE HEADING, NEVER A CHILD OF IT — `Labelled`'s rule and its
                  reason: a `<button>` inside the `h2` joins the heading's accessible name,
                  so somebody navigating by headings hears the title and then "About …".
                  It sits BEFORE `context` because it explains the title rather than
                  whatever date or period the screen is currently showing. */}
              {why ? <Explains about={title} why={why} /> : null}
              {context}
            </div>
            {lede ? (
              <p className="m-0 max-w-(--frame-prose) text-shell-body text-muted-foreground">
                {lede}
              </p>
            ) : null}
          </div>
          {actions ? (
            <div className="flex min-w-0 max-w-full shrink-0 flex-wrap items-center gap-2">
              {actions}
            </div>
          ) : null}
        </div>
      </div>
      {/* BELOW the rule and above the content: the tabs belong to what follows, not to
          the heading, and putting them under the border is what says so. */}
      {tabs}
      {children}
    </section>
  );
}

/**
 * A titled region within a page.
 *
 * `h3`, because the page's own title is the `h2` under the shell's `h1`. A screen that
 * reached for `h2` again would flatten its own outline, and the heading order is how
 * somebody navigating by headings understands where they are.
 */
export function Panel({
  title,
  note,
  children,
}: {
  readonly title: string;
  readonly note?: string;
  readonly children: ReactNode;
}) {
  return (
    <section className="flex flex-col gap-3">
      <h3 className="m-0 text-[length:var(--type-shell-label-size)] leading-[var(--type-shell-label-leading)] font-medium tracking-[var(--type-shell-label-tracking)] text-foreground">
        {title}
      </h3>
      {note ? (
        <p className="m-0 max-w-(--frame-prose) text-shell-body text-muted-foreground">{note}</p>
      ) : null}
      {children}
    </section>
  );
}
